import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, Close, DeleteOutline, EditOutlined, Search } from "@material-ui/icons";

const getBackendBaseUrl = () => {
  const configured = (window.localStorage.getItem("openimis_backend_base_url") || "").trim();
  if (configured) return configured.endsWith("/") ? configured.slice(0, -1) : configured;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:3000`;
  }
  return `${window.location.protocol}//${window.location.host}`;
};

const looksLikeJwt = (value) => {
  const token = String(value || "").trim();
  return /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token);
};

const deepFindToken = (node, depth = 0) => {
  if (depth > 6 || node == null) return "";

  if (typeof node === "string") {
    const trimmed = node.trim();
    if (looksLikeJwt(trimmed)) return trimmed;
    if (/^Bearer\s+/i.test(trimmed)) {
      const candidate = trimmed.replace(/^Bearer\s+/i, "").trim();
      if (looksLikeJwt(candidate)) return candidate;
    }
    return "";
  }

  if (Array.isArray(node)) {
    for (const item of node) {
      const found = deepFindToken(item, depth + 1);
      if (found) return found;
    }
    return "";
  }

  if (typeof node === "object") {
    const tokenKeys = ["token", "jwt", "accessToken", "access_token", "idToken", "id_token"];
    for (const key of tokenKeys) {
      if (key in node) {
        const found = deepFindToken(node[key], depth + 1);
        if (found) return found;
      }
    }
    for (const value of Object.values(node)) {
      const found = deepFindToken(value, depth + 1);
      if (found) return found;
    }
  }

  return "";
};

const getSharedAuthToken = () => {
  const directKeys = ["openimis_token", "token", "jwt_token", "access_token"];
  for (const key of directKeys) {
    const value = (window.localStorage.getItem(key) || "").trim();
    if (looksLikeJwt(value)) return value;
    if (/^Bearer\s+/i.test(value)) {
      const candidate = value.replace(/^Bearer\s+/i, "").trim();
      if (looksLikeJwt(candidate)) return candidate;
    }
  }

  try {
    const serialized = window.localStorage.getItem("state");
    if (!serialized) return "";
    const parsed = JSON.parse(serialized);
    return deepFindToken(parsed);
  } catch (error) {
    return "";
  }
};

const buildApiRoots = (baseUrl) => {
  const raw = String(baseUrl || "").trim().replace(/\/$/, "");
  if (!raw) return [];

  const baseCandidates = [raw];
  if (raw.endsWith("/api")) baseCandidates.push(raw.slice(0, -4));
  if (raw.endsWith("/front")) baseCandidates.push(raw.slice(0, -6));

  try {
    const parsed = new URL(raw);
    baseCandidates.push(`${parsed.protocol}//${parsed.host}`);
  } catch (error) {
    // Ignore malformed URL and keep raw candidates only.
  }

  const uniqueBases = [...new Set(baseCandidates.filter(Boolean).map((x) => x.replace(/\/$/, "")))];
  const roots = [];
  for (const base of uniqueBases) {
    roots.push(base.endsWith("/api") ? base : `${base}/api`);
  }
  return [...new Set(roots.map((x) => x.replace(/\/$/, "")))];
};

const buildEligibilityEndpointCandidates = (baseUrl) => {
  const apiRoots = buildApiRoots(baseUrl);
  return {
    options: apiRoots.flatMap((root) => [
      `${root}/sync/eligibility-rules/options/`,
      `${root}/social_protection/eligibility_rules/options/`,
    ]),
    socialOptions: apiRoots.flatMap((root) => [`${root}/sync/social-assessment-rules/options/`]),
    rules: apiRoots.flatMap((root) => [`${root}/sync/eligibility-rules/`, `${root}/social_protection/eligibility_rules/`]),
    socialRules: apiRoots.flatMap((root) => [`${root}/sync/social-assessment-rules/`]),
    ruleDetail: (id) =>
      apiRoots.flatMap((root) => [
        `${root}/social_protection/eligibility_rules/${id}/`,
        `${root}/sync/eligibility-rules/${id}/`,
      ]),
  };
};

const FIELD_TYPE_LABELS = {
  text: "Text",
  multiline_text: "Paragraph",
  number: "Numeric",
  date: "Date",
  boolean: "Boolean",
  choice_buttons: "Choice (buttons)",
  choice_dropdown: "Choice (dropdown)",
  remark: "Remark / Notes",
};

const CONDITION_OPERATORS = ["eq", "ne", "lt", "lte", "gt", "gte", "contains", "in", "not_in"];

const asStringMap = (value) => {
  if (!value || typeof value !== "object") return {};
  return value;
};

const extractPayloadMap = (body) => {
  const data = body?.data;
  const result = body?.result;
  if (data && typeof data === "object" && !Array.isArray(data)) return data;
  if (result && typeof result === "object" && !Array.isArray(result)) return result;
  return body || {};
};

const extractMapList = (root, keys) => {
  for (const key of keys) {
    const raw = root?.[key];
    if (Array.isArray(raw)) return raw.map((item) => asStringMap(item));
  }
  return [];
};

const entityUuid = (item = {}, fallbackKeys = []) => {
  const raw = item.uuid || item.id || item.pk || fallbackKeys.map((key) => item[key]).find((v) => v != null);
  const value = String(raw || "").trim();
  return value || null;
};

const entityCode = (item = {}, fallbackKeys = []) => {
  const raw = item.code || fallbackKeys.map((key) => item[key]).find((v) => v != null) || "";
  return String(raw).trim();
};

const entityName = (item = {}, fallbackKeys = []) => {
  const raw = item.name || item.label || fallbackKeys.map((key) => item[key]).find((v) => v != null) || "";
  const name = String(raw).trim();
  return name || "-";
};

const toInt = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

// Unlike toInt(), a blank string here stays null instead of becoming 0
// (Number("") === 0 in JS, which used to make "left blank" indistinguishable
// from "admin typed 0" for Min/Max Age — and 0 is a real, honorable age
// boundary, e.g. min_age:0/max_age:0 means "infants under 1 year only").
// Mirrors normalizeOptionalInt in customAdminMenuModule.js, which solves the
// identical problem correctly for the Social Assessment builder's age fields.
const normalizeOptionalAge = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const normalizeServiceIdForPayload = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : raw;
  }
  return raw;
};

const extractRuleServiceIds = (rule) => {
  const fromIds = Array.isArray(rule?.service_ids)
    ? rule.service_ids.map((id) => String(id ?? "").trim()).filter(Boolean)
    : [];
  if (fromIds.length) return [...new Set(fromIds)];

  const services = Array.isArray(rule?.services) ? rule.services : [];
  const fromServices = services
    .map((svc) => entityUuid(asStringMap(svc), ["service_id"]))
    .filter(Boolean)
    .map((id) => String(id).trim());
  return [...new Set(fromServices)];
};

const coerceBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    return lowered === "true" || lowered === "1" || lowered === "yes" || lowered === "y";
  }
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return coerceBool(value.required) || coerceBool(value.mandatory);
  }
  return false;
};

const normalizeRuleFieldType = (rawType) => {
  const type = String(rawType || "text").trim().toLowerCase();
  switch (type) {
    case "numeric":
    case "int":
    case "integer":
    case "float":
    case "double":
    case "decimal":
      return "number";
    case "datetime":
    case "date_time":
    case "date_picker":
    case "datepicker":
    case "date-picker":
      return "date";
    case "bool":
    case "yes_no":
      return "boolean";
    case "textarea":
    case "text_area":
    case "paragraph":
      return "multiline_text";
    case "dropdown":
    case "select":
      return "choice_dropdown";
    case "choice":
    case "chips":
      return "choice_buttons";
    default:
      return type || "text";
  }
};

const fieldFromJson = (json = {}, schemaVersion = 1) => {
  const computed = asStringMap(json.computed || json.computed_from || json.calculation || {});
  const parsedType = schemaVersion <= 1 ? String(json.type || "text").toLowerCase() : normalizeRuleFieldType(json.type);
  return {
    key: String(json.key || "field_unknown"),
    label: String(json.label || ""),
    labelMy: String(json.label_my || json.labelMy || ""),
    type: parsedType,
    order: toInt(json.order) || 1,
    required: Boolean(json.required),
    indication: String(json.indication || ""),
    section: String(json.section || ""),
    helperText: String(json.helper_text || json.helpText || ""),
    unit: String(json.unit || ""),
    choices: Array.isArray(json.choices) ? json.choices.map((c) => String(c)) : [],
    // Only meaningful for choice_buttons/choice_dropdown — lets the
    // beneficiary/officer pick more than one option on the tablet.
    multiple: Boolean(json.multiple),
    visibleWhen: Array.isArray(json.visible_when) ? json.visible_when.map((x) => conditionFromJson(x)) : [],
    requiredWhen: Array.isArray(json.required_when) ? json.required_when.map((x) => conditionFromJson(x)) : [],
    computedType: String(computed.type || ""),
    computedOperator: String(computed.operator || computed.operation || (computed.type === "date_diff_days" ? "-" : "")),
    computedFromField: String(computed.right_field || computed.from_field || computed.start_field || ""),
    computedToField: String(computed.left_field || computed.to_field || computed.end_field || ""),
    computedFormula: String(computed.formula || ""),
  };
};

const conditionFromJson = (json = {}) => ({
  field: String(json.field || "insuree.age"),
  operator: String(json.operator || "eq"),
  value: `${json.value ?? ""}`,
  connector: json.connector === "or" ? "or" : "and",
});

const coerceConditionValue = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  if (trimmed.toLowerCase() === "true") return true;
  if (trimmed.toLowerCase() === "false") return false;
  const asNum = Number(trimmed);
  if (Number.isFinite(asNum)) return asNum;
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return trimmed;
    }
  }
  return trimmed;
};

const conditionToJson = (condition) => ({
  field: condition.field,
  operator: condition.operator,
  value: coerceConditionValue(condition.value),
  connector: condition.connector === "or" ? "or" : "and",
});

const blankFieldDraft = (nextOrder = 1) => ({
  key: "",
  label: "",
  labelMy: "",
  type: "text",
  order: nextOrder,
  required: false,
  indication: "",
  section: "",
  helperText: "",
  unit: "",
  choices: [],
  multiple: false,
  visibleWhen: [],
  requiredWhen: [],
  computedType: "",
  computedOperator: "-",
  computedFromField: "",
  computedToField: "",
  computedFormula: "",
});

export default function EligibilityRulesManagerPage() {
  const [baseUrl, setBaseUrl] = React.useState(getBackendBaseUrl());
  const [token, setToken] = React.useState(() => getSharedAuthToken());
  const [message, setMessage] = React.useState("");

  const [loading, setLoading] = React.useState(false);

  const [benefitPlans, setBenefitPlans] = React.useState([]);
  const [diagnoses, setDiagnoses] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [rules, setRules] = React.useState([]);

  const [selectedRuleId, setSelectedRuleId] = React.useState(null);
  const [selectedBenefitPlanId, setSelectedBenefitPlanId] = React.useState("");
  const [selectedDiagnosisId, setSelectedDiagnosisId] = React.useState("");
  const [selectedServiceIds, setSelectedServiceIds] = React.useState([]);

  const [name, setName] = React.useState("");
  const [screenName, setScreenName] = React.useState("Main Screen");
  const [screenOrder, setScreenOrder] = React.useState("1");
  const [enabled, setEnabled] = React.useState(true);
  const [mandatoryAttachmentRequired, setMandatoryAttachmentRequired] = React.useState(false);
  const [systemAction, setSystemAction] = React.useState("allow");
  const [genderScope, setGenderScope] = React.useState("any");
  const [minAge, setMinAge] = React.useState("");
  const [maxAge, setMaxAge] = React.useState("");

  const [fields, setFields] = React.useState([]);
  const [conditions, setConditions] = React.useState([]);

  const [serviceDialogOpen, setServiceDialogOpen] = React.useState(false);
  const [serviceSearch, setServiceSearch] = React.useState("");
  const [serviceDraftIds, setServiceDraftIds] = React.useState([]);

  const [diagnosisDialogOpen, setDiagnosisDialogOpen] = React.useState(false);
  const [diagnosisDraftCode, setDiagnosisDraftCode] = React.useState("");
  const [diagnosisDraftName, setDiagnosisDraftName] = React.useState("");
  const [diagnosisEditingId, setDiagnosisEditingId] = React.useState(null);
  const [diagnosisSearch, setDiagnosisSearch] = React.useState("");

  const [fieldDialogOpen, setFieldDialogOpen] = React.useState(false);
  const [fieldEditingKey, setFieldEditingKey] = React.useState(null);
  const [fieldDraft, setFieldDraft] = React.useState(blankFieldDraft(1));
  const [choiceDraft, setChoiceDraft] = React.useState("");

  const [conditionDialogOpen, setConditionDialogOpen] = React.useState(false);
  const [conditionDialogTitle, setConditionDialogTitle] = React.useState("");
  const [conditionDialogTarget, setConditionDialogTarget] = React.useState("visible");
  const [conditionDialogDraft, setConditionDialogDraft] = React.useState([]);

  React.useEffect(() => {
    const uri = new URL(window.location.href);
    const rootQuery = uri.searchParams;
    const hash = uri.hash || "";
    let queryToken = (rootQuery.get("token") || "").trim();
    let queryBase = (rootQuery.get("baseUrl") || "").trim();

    if ((!queryToken || !queryBase) && hash.includes("?")) {
      const fragmentQuery = hash.split("?").slice(1).join("?").trim();
      if (fragmentQuery) {
        const fragmentParams = new URLSearchParams(fragmentQuery);
        queryToken = queryToken || (fragmentParams.get("token") || "").trim();
        queryBase = queryBase || (fragmentParams.get("baseUrl") || "").trim();
      }
    }

    if (queryBase) setBaseUrl(queryBase);
    if (queryToken) {
      setToken(queryToken);
      setTimeout(() => {
        loadOptions(queryBase || baseUrl);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableConditionFields = React.useMemo(() => {
    const builtIns = [
      { key: "insuree.age", label: "Age" },
      { key: "insuree.age_months", label: "Age (months)" },
      { key: "insuree.age_days", label: "Age (days)" },
      { key: "insuree.gender", label: "Gender" },
      { key: "claim.diagnosis_id", label: "Selected diagnosis" },
      { key: "claim.benefit_plan_id", label: "Selected benefit plan" },
      { key: "form.gravida", label: "Gravida" },
      { key: "form.parity", label: "Parity" },
      { key: "form.lmp", label: "LMP" },
      { key: "form.edd", label: "EDD" },
      { key: "form.gestational_age", label: "Gestational Age" },
      { key: "form.height", label: "Height" },
      { key: "form.weight", label: "Weight" },
      { key: "form.blood_pressure", label: "Blood Pressure" },
      { key: "form.temperature", label: "Temperature" },
    ];
    const custom = fields.map((field) => ({ key: `form.${field.key}`, label: field.label }));
    return [...builtIns, ...custom];
  }, [fields]);

  const selectedServices = React.useMemo(
    () =>
      services.filter((service) => {
        const id = entityUuid(service, ["service_id"]);
        return id && selectedServiceIds.includes(id);
      }),
    [services, selectedServiceIds],
  );

  const authHeaders = React.useMemo(() => {
    const t = token.trim();
    if (!t) return {};
    return { Authorization: `Bearer ${t}` };
  }, [token]);

  const show = (msg) => setMessage(msg);

  const loadOptions = async (baseOverride) => {
    const resolvedBase = (baseOverride ?? baseUrl).trim();
    if (!resolvedBase) {
      show("Base URL is required");
      return;
    }

    setLoading(true);
    try {
      const endpoints = buildEligibilityEndpointCandidates(resolvedBase);
      const isSocialAssessment = systemAction === "social_assessment";
      const optionCandidates = isSocialAssessment
        ? [...endpoints.socialOptions, ...endpoints.options]
        : endpoints.options;
      let lastErrorStatus = null;

      for (const url of optionCandidates) {
        const res = await fetch(url, {
          headers: { ...authHeaders },
          credentials: "include",
        });

        if (!res.ok) {
          lastErrorStatus = res.status;
          continue;
        }

        const body = asStringMap(await res.json());
        const root = extractPayloadMap(body);
        const loadedPlans = extractMapList(root, ["benefit_plans", "benefitPlans", "benefit_plan", "benefitPlan", "plans"]);
        const loadedDiagnoses = extractMapList(root, ["diagnoses", "diagnosis"]);
        const loadedServices = extractMapList(root, ["services", "service"]);

        setBenefitPlans(loadedPlans);
        setDiagnoses(loadedDiagnoses);
        setServices(loadedServices);

        show(`Loaded ${loadedPlans.length} benefit plans, ${loadedDiagnoses.length} diagnoses, ${loadedServices.length} services`);
        return;
      }

      if (lastErrorStatus === 404) {
        show("Endpoint not found (404): checked /api/sync/eligibility-rules/options/, /api/sync/social-assessment-rules/options/, and /api/social_protection/eligibility_rules/options/. Check API base URL and deployed modules.");
      } else {
        show(`Failed to load diagnosis/services. Status ${lastErrorStatus ?? "unknown"}`);
      }
    } catch (error) {
      show(`Failed to load diagnosis/services. Check base URL, login session, token (if used), and backend.\n${error}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async () => {
    if (!selectedBenefitPlanId) {
      show("Select a benefit plan first");
      return;
    }

    setLoading(true);
    try {
      const endpoints = buildEligibilityEndpointCandidates(baseUrl);
      const isSocialAssessment = systemAction === "social_assessment";
      const ruleCandidates = isSocialAssessment ? endpoints.socialRules : endpoints.rules;
      let lastErrorStatus = null;

      for (const endpoint of ruleCandidates) {
        const url = `${endpoint}?benefit_plan_id=${encodeURIComponent(selectedBenefitPlanId)}`;
        const res = await fetch(url, {
          headers: { ...authHeaders },
          credentials: "include",
        });

        if (!res.ok) {
          lastErrorStatus = res.status;
          continue;
        }

        const body = asStringMap(await res.json());
        const loadedRules = Array.isArray(body.rules) ? body.rules.map((r) => asStringMap(r)) : [];
        setRules(loadedRules);
        show(`Loaded ${loadedRules.length} rule(s)`);
        return;
      }

      if (lastErrorStatus === 404) {
        show("Endpoint not found (404): checked /api/sync/eligibility-rules/, /api/sync/social-assessment-rules/, and /api/social_protection/eligibility_rules/. Check API base URL and deployed modules.");
      } else {
        show(`Failed to load rules: status ${lastErrorStatus ?? "unknown"}`);
      }
    } catch (error) {
      show(`Failed to load rules: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = (resetBenefitPlan = true) => {
    setSelectedRuleId(null);
    setName("");
    setScreenName("Main Screen");
    setScreenOrder("1");
    setEnabled(true);
    setMandatoryAttachmentRequired(false);
    setSystemAction(systemAction === "social_assessment" ? "social_assessment" : "allow");
    setGenderScope("any");
    setMinAge("");
    setMaxAge("");
    setSelectedDiagnosisId("");
    if (resetBenefitPlan) setSelectedBenefitPlanId("");
    setSelectedServiceIds([]);
    setFields([]);
    setConditions([]);
  };

  const buildUiSchema = () => {
    const ordered = [...fields].sort((a, b) => a.order - b.order);
    const parsedMinAge = normalizeOptionalAge(minAge);
    const parsedMaxAge = normalizeOptionalAge(maxAge);
    return {
      schema_version: 2,
      version: 2,
      applicability: {
        gender: genderScope,
        min_age: parsedMinAge,
        max_age: parsedMaxAge,
      },
      screen: {
        name: screenName.trim() || "Main Screen",
        order: toInt(screenOrder.trim()) || 1,
      },
      fields: ordered.map((field) => ({
        key: field.key,
        label: field.label,
        label_my: field.labelMy || "",
        type: field.type,
        order: field.order,
        required: field.required,
        indication: field.indication || "",
        section: field.section,
        helper_text: field.helperText,
        unit: field.unit,
        choices: field.choices,
        multiple: (field.type === "choice_buttons" || field.type === "choice_dropdown") ? Boolean(field.multiple) : false,
        visible_when: field.visibleWhen.map(conditionToJson),
        required_when: field.requiredWhen.map(conditionToJson),
        ...(field.computedType.trim()
          ? {
              computed: field.computedType === "formula"
                ? { type: "formula", formula: field.computedFormula }
                : {
                    type: field.computedType,
                    operator: field.computedOperator,
                    left_field: field.computedToField,
                    right_field: field.computedFromField,
                    from_field: field.computedFromField,
                    to_field: field.computedToField,
                  },
            }
          : {}),
      })),
    };
  };

  const saveRule = async () => {
    if (!selectedBenefitPlanId) {
      show("Benefit plan is required");
      return;
    }
    if (!name.trim()) {
      show("Rule name is required");
      return;
    }
    if (!fields.length) {
      show("Add at least one screen field");
      return;
    }

    const parsedMinAge = normalizeOptionalAge(minAge);
    const parsedMaxAge = normalizeOptionalAge(maxAge);
    if (parsedMinAge != null && parsedMinAge < 0) {
      show("Min age must be 0 or greater");
      return;
    }
    if (parsedMaxAge != null && parsedMaxAge < 0) {
      show("Max age must be 0 or greater");
      return;
    }
    if (parsedMinAge != null && parsedMaxAge != null && parsedMinAge > parsedMaxAge) {
      show("Min age cannot be greater than max age");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        benefit_plan_id: selectedBenefitPlanId,
        name: name.trim(),
        enabled,
        gender: genderScope,
        min_age: parsedMinAge,
        max_age: parsedMaxAge,
        diagnosis_id: selectedDiagnosisId || null,
        service_ids: selectedServiceIds
          .map((id) => normalizeServiceIdForPayload(id))
          .filter((id) => id != null),
        conditions: conditions.map(conditionToJson),
        ui_schema: buildUiSchema(),
        mandatory_attachment_required: mandatoryAttachmentRequired,
        mandatory_attachments: mandatoryAttachmentRequired,
        system_action: systemAction,
      };

      const isUpdate = Boolean(selectedRuleId);
      const endpoints = buildEligibilityEndpointCandidates(baseUrl);
      const writeAttempts = isUpdate
        ? [
            { method: "PUT", urls: endpoints.ruleDetail(selectedRuleId), mode: "update" },
            { method: "PATCH", urls: endpoints.ruleDetail(selectedRuleId), mode: "update" },
            { method: "POST", urls: endpoints.rules, mode: "create-version" },
          ]
        : [{ method: "POST", urls: endpoints.rules, mode: "create" }];

      let saved = false;
      let authDenied = false;
      let sawMethodNotAllowed = false;
      let lastError = "";
      let savedMode = isUpdate ? "update" : "create";

      for (const attempt of writeAttempts) {
        if (saved) break;
        for (const url of attempt.urls) {
          const res = await fetch(url, {
            method: attempt.method,
            headers: { "Content-Type": "application/json", ...authHeaders },
            credentials: "include",
            body: JSON.stringify(payload),
          });

          if (res.status === 401 || res.status === 403) {
            authDenied = true;
            continue;
          }
          if (res.status === 405) {
            sawMethodNotAllowed = true;
            continue;
          }

          const responseText = await res.text();
          let parsedBody = null;
          if (responseText) {
            try {
              parsedBody = JSON.parse(responseText);
            } catch (error) {
              parsedBody = null;
            }
          }

          if (!res.ok) {
            const detail = parsedBody?.detail || parsedBody?.message || responseText;
            lastError = `${attempt.mode} failed (${res.status}): ${detail}`;
            continue;
          }

          if (parsedBody && parsedBody.success === false) {
            const detail = parsedBody?.detail || parsedBody?.message || "Server returned success=false";
            lastError = `${attempt.mode} failed: ${detail}`;
            continue;
          }

          saved = true;
          savedMode = attempt.mode;
          break;
        }
      }

      if (!saved) {
        if (authDenied) {
          throw new Error("Server denied write access. Check session login or token.");
        }
        if (sawMethodNotAllowed) {
          throw new Error("Backend routes are reachable but write is not enabled for this deployment (method not allowed). Enable social_protection write endpoints for create/update.");
        }
        throw new Error(lastError || `${isUpdate ? "Update" : "Create"} failed`);
      }

      if (savedMode === "create-version" && isUpdate) {
        show("Rule version created (update endpoint was not writable)");
      } else {
        show(isUpdate ? "Rule updated" : "Rule created");
      }
      await loadRules();
      clearForm(false);
    } catch (error) {
      show(`Failed to save rule: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const editRule = (rule) => {
    const uiSchema = asStringMap(rule.ui_schema);
    const benefitPlan = asStringMap(rule.benefit_plan);
    const diagnosis = asStringMap(rule.diagnosis);
    const schemaVersion = toInt(rule.schema_version) || toInt(uiSchema.schema_version) || toInt(uiSchema.version) || 1;
    const screen = asStringMap(uiSchema.screen);
    const applicability = asStringMap(uiSchema.applicability);
    const rawFields = Array.isArray(uiSchema.fields) ? uiSchema.fields : [];
    const rawConditions = Array.isArray(rule.conditions) ? rule.conditions : [];

    setSelectedRuleId(entityUuid(rule, ["rule_id"]));
    setName(String(rule.name || ""));
    setScreenName(String(screen.name || "Main Screen"));
    setScreenOrder(String(screen.order ?? 1));
    setEnabled(Boolean(rule.enabled ?? true));
    setSystemAction(String(rule.system_action || "allow"));
    setGenderScope(String(rule.gender || applicability.gender || "any"));
    setMinAge(
      rule.min_age != null
        ? String(rule.min_age)
        : (applicability.min_age != null ? String(applicability.min_age) : ""),
    );
    setMaxAge(
      rule.max_age != null
        ? String(rule.max_age)
        : (applicability.max_age != null ? String(applicability.max_age) : ""),
    );
    setSelectedBenefitPlanId(entityUuid(benefitPlan, ["benefit_plan_id"]) || "");
    setSelectedDiagnosisId(entityUuid(diagnosis, ["diagnosis_id"]) || "");
    setSelectedServiceIds(extractRuleServiceIds(rule));
    setMandatoryAttachmentRequired(coerceBool(rule.mandatory_attachment_required) || coerceBool(rule.mandatory_attachments));
    setFields(rawFields.map((item) => fieldFromJson(asStringMap(item), schemaVersion)));
    setConditions(rawConditions.map((item) => conditionFromJson(asStringMap(item))));
  };

  const openServiceDialog = () => {
    setServiceDraftIds([...selectedServiceIds]);
    setServiceSearch("");
    setServiceDialogOpen(true);
  };

  const filteredServices = React.useMemo(() => {
    const term = serviceSearch.trim().toLowerCase();
    if (!term) return services;
    return services.filter((service) => `${service.code || ""} ${service.name || ""}`.toLowerCase().includes(term));
  }, [services, serviceSearch]);

  const toggleServiceDraft = (id, checked) => {
    if (checked) {
      if (!serviceDraftIds.includes(id)) setServiceDraftIds((prev) => [...prev, id]);
      return;
    }
    setServiceDraftIds((prev) => prev.filter((x) => x !== id));
  };

  const openFieldDialog = (existing) => {
    if (existing) {
      setFieldEditingKey(existing.key);
      setFieldDraft({ ...existing, choices: [...existing.choices], visibleWhen: [...existing.visibleWhen], requiredWhen: [...existing.requiredWhen] });
    } else {
      setFieldEditingKey(null);
      setFieldDraft(blankFieldDraft(fields.length + 1));
    }
    setChoiceDraft("");
    setFieldDialogOpen(true);
  };

  const saveFieldDialog = () => {
    if (!fieldDraft.label.trim()) return;

    const finalized = {
      ...fieldDraft,
      key: fieldEditingKey || `field_${Date.now()}`,
      label: fieldDraft.label.trim(),
      labelMy: (fieldDraft.labelMy || "").trim(),
      indication: (fieldDraft.indication || "").trim(),
      section: fieldDraft.section.trim(),
      helperText: fieldDraft.helperText.trim(),
      unit: fieldDraft.unit.trim(),
      order: toInt(fieldDraft.order) || (fields.length + 1),
      computedType: fieldDraft.type === "number" && fieldDraft.computedType
        ? (fieldDraft.computedType === "formula" ? "formula" : "binary_formula")
        : "",
    };

    if (fieldEditingKey) {
      setFields((prev) => prev.map((field) => (field.key === fieldEditingKey ? finalized : field)));
    } else {
      setFields((prev) => [...prev, finalized]);
    }
    setFieldDialogOpen(false);
  };

  const removeField = (fieldKey) => {
    setFields((prev) => prev.filter((field) => field.key !== fieldKey));
    setConditions((prev) => prev.filter((condition) => condition.field !== `form.${fieldKey}`));
  };

  const openConditionListDialog = (title, target, initialConditions) => {
    setConditionDialogTitle(title);
    setConditionDialogTarget(target);
    setConditionDialogDraft(initialConditions.map((c) => ({ ...c })));
    setConditionDialogOpen(true);
  };

  const applyConditionListDialog = () => {
    if (conditionDialogTarget === "rule") {
      setConditions(conditionDialogDraft.map((c) => ({ ...c })));
    } else {
      setFieldDraft((prev) => ({
        ...prev,
        [conditionDialogTarget]: conditionDialogDraft.map((c) => ({ ...c })),
      }));
    }
    setConditionDialogOpen(false);
  };

  const conditionFieldOptions = availableConditionFields.length ? availableConditionFields : [{ key: "insuree.age", label: "Age" }];

  const computableFields = fields.filter((field) => (field.type === "date" || field.type === "number") && field.key !== fieldEditingKey);

  React.useEffect(() => {
    if (token.trim()) {
      show("Using shared app token from existing session.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDiagnosisDialog = (diag = null) => {
    setDiagnosisEditingId(diag ? diag.id : null);
    setDiagnosisDraftCode(diag ? String(diag.code || "") : "");
    setDiagnosisDraftName(diag ? String(diag.name || "") : "");
    setDiagnosisDialogOpen(true);
  };

  const saveDiagnosis = async () => {
    const code = diagnosisDraftCode.trim();
    const name = diagnosisDraftName.trim();
    if (!code || !name) { show("Code and name are required."); return; }
    if (code.length > 6) { show("Code must be 6 characters or fewer."); return; }

    const diagBase = `${baseUrl.trim()}/api/sync/diagnoses`;
    const isEdit = diagnosisEditingId != null;
    const url = isEdit ? `${diagBase}/${diagnosisEditingId}/` : `${diagBase}/`;
    const method = isEdit ? "PUT" : "POST";

    setLoading(true);
    try {
      let res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...authHeaders },
        credentials: "include",
        body: JSON.stringify({ code, name }),
      });
      let body = await res.json().catch(() => ({}));

      // Code belongs to a soft-deleted diagnosis — ask before silently
      // reactivating it (which would overwrite its old name with the new
      // one just typed). Only relevant on create; PUT edits an id directly.
      if (!isEdit && res.status === 409 && body.soft_deleted_match) {
        const reuse = window.confirm(
          `Code "${code}" was previously used by a deleted diagnosis named ` +
            `"${body.existing_name}". Reuse that record with the new name ` +
            `"${name}" instead of creating a separate one?`,
        );
        if (!reuse) {
          show("Cancelled — choose a different code, or confirm reuse to proceed.");
          return;
        }
        res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", ...authHeaders },
          credentials: "include",
          body: JSON.stringify({ code, name, confirm_reuse: true }),
        });
        body = await res.json().catch(() => ({}));
      }

      if (!res.ok) {
        show(body.detail || `Failed (${res.status})`);
        return;
      }
      setDiagnoses((prev) =>
        isEdit
          ? prev.map((d) => (d.id === diagnosisEditingId ? body : d))
          : [...prev, body].sort((a, b) => String(a.code).localeCompare(String(b.code))),
      );
      setDiagnosisDialogOpen(false);
      show(isEdit ? "Diagnosis updated." : "Diagnosis created.");
    } catch (err) {
      show(`Error: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteDiagnosis = async (diag) => {
    if (!window.confirm(`Delete diagnosis "${diag.code} – ${diag.name}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl.trim()}/api/sync/diagnoses/${diag.id}/`, {
        method: "DELETE",
        headers: { ...authHeaders },
        credentials: "include",
      });
      if (res.status === 204) {
        setDiagnoses((prev) => prev.filter((d) => d.id !== diag.id));
        if (String(selectedDiagnosisId) === String(diag.id)) setSelectedDiagnosisId("");
        show("Diagnosis deleted.");
      } else {
        const body = await res.json().catch(() => ({}));
        show(body.detail || `Failed (${res.status})`);
      }
    } catch (err) {
      show(`Error: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper square style={{ backgroundColor: "#0F766E", color: "#fff", padding: "12px 16px" }}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>Eligibility Rule Builder</Typography>
      </Paper>

      <Box style={{ backgroundColor: "#F5F7FB", minHeight: "calc(100vh - 64px)", padding: 16 }}>
        {!!message && (
          <Paper style={{ padding: 10, marginBottom: 12 }}>
            <Typography style={{ color: "#0f172a" }}>{message}</Typography>
          </Paper>
        )}

        <Box display="flex" style={{ gap: 16, alignItems: "flex-start" }}>
          <Box style={{ flex: 7, minWidth: 0 }}>
            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Data</Typography>
                <Box display="grid" gridGap={12}>
                  <Box display="flex" style={{ gap: 8 }}>
                    <Button variant="contained" color="primary" disabled={loading} onClick={() => loadOptions()}>
                      Load Diagnosis & Services
                    </Button>
                    <Button variant="outlined" disabled={loading} onClick={loadRules}>
                      Load Existing Rules
                    </Button>
                    <Button onClick={() => clearForm(true)}>New Rule</Button>
                  </Box>
                  <Typography style={{ color: "#444" }}>
                    Uses the same in-app session and shared token automatically.
                  </Typography>
                  {loading && <LinearProgress />}
                </Box>
              </CardContent>
            </Card>

            {/* ── Diagnosis Management ────────────────────────────────── */}
            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 14 }}>
                  <Typography style={{ fontSize: 18, fontWeight: 800 }}>
                    Diagnoses
                    {diagnoses.length > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 500, color: "#64748B", marginLeft: 8 }}>
                        {diagnoses.length} total
                      </span>
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => openDiagnosisDialog(null)}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 700 }}
                  >
                    Add Diagnosis
                  </Button>
                </Box>

                {diagnoses.length > 0 && (
                  <TextField
                    placeholder="Search by code or name…"
                    value={diagnosisSearch}
                    onChange={(e) => setDiagnosisSearch(e.target.value)}
                    fullWidth
                    size="small"
                    style={{ marginBottom: 10 }}
                    InputProps={{
                      startAdornment: <Search style={{ color: "#94A3B8", marginRight: 6, fontSize: 18 }} />,
                    }}
                  />
                )}

                {diagnoses.length === 0 && (
                  <Typography style={{ color: "#94A3B8", fontSize: 14, textAlign: "center", padding: "20px 0" }}>
                    No diagnoses loaded. Click "Load Diagnosis & Services" or "Add Diagnosis".
                  </Typography>
                )}

                <Box style={{ maxHeight: 320, overflowY: "auto" }}>
                  {diagnoses
                    .filter((d) => {
                      const q = diagnosisSearch.toLowerCase();
                      return !q || String(d.code || "").toLowerCase().includes(q) || String(d.name || "").toLowerCase().includes(q);
                    })
                    .map((diag) => (
                      <Box
                        key={diag.id}
                        display="flex"
                        alignItems="center"
                        style={{
                          padding: "10px 4px",
                          borderBottom: "1px solid #F1F5F9",
                          gap: 10,
                        }}
                      >
                        <Box style={{ background: "#EFF6FF", borderRadius: 6, padding: "2px 8px", flexShrink: 0 }}>
                          <Typography style={{ fontSize: 12, fontWeight: 700, color: "#1D4ED8", fontFamily: "monospace" }}>
                            {diag.code}
                          </Typography>
                        </Box>
                        <Typography style={{ fontSize: 14, color: "#111827", flex: 1 }}>{diag.name}</Typography>
                        <IconButton size="small" onClick={() => openDiagnosisDialog(diag)} style={{ color: "#0F766E" }}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteDiagnosis(diag)} style={{ color: "#EF4444" }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
              </CardContent>
            </Card>

            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Rule Setup</Typography>
                <Box display="grid" gridGap={12}>
                  <TextField
                    select
                    label="Benefit Plan"
                    value={selectedBenefitPlanId}
                    onChange={(e) => setSelectedBenefitPlanId(e.target.value)}
                    fullWidth
                  >
                    {benefitPlans
                      .map((item) => {
                        const id = entityUuid(item, ["benefit_plan_id"]);
                        if (!id) return null;
                        const code = entityCode(item, ["benefit_plan_code"]);
                        const title = entityName(item, ["benefit_plan_name"]);
                        return (
                          <MenuItem key={id} value={id}>
                            {code ? `${code} - ${title}` : title}
                          </MenuItem>
                        );
                      })
                      .filter(Boolean)}
                  </TextField>
                  <TextField label="Rule Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
                  <Box display="flex" style={{ gap: 12 }}>
                    <TextField label="Screen Name" value={screenName} onChange={(e) => setScreenName(e.target.value)} fullWidth />
                    <TextField
                      label="Order"
                      value={screenOrder}
                      onChange={(e) => setScreenOrder(e.target.value)}
                      style={{ width: 120 }}
                    />
                  </Box>
                  <Box display="flex" style={{ gap: 12 }}>
                    <TextField
                      select
                      label="Diagnosis"
                      value={selectedDiagnosisId}
                      onChange={(e) => setSelectedDiagnosisId(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Any diagnosis</MenuItem>
                      {diagnoses
                        .map((item) => {
                          const id = entityUuid(item, ["diagnosis_id"]);
                          if (!id) return null;
                          const code = entityCode(item, ["diagnosis_code"]);
                          const title = entityName(item, ["diagnosis_name"]);
                          return (
                            <MenuItem key={id} value={id}>
                              {code ? `${code} - ${title}` : title}
                            </MenuItem>
                          );
                        })
                        .filter(Boolean)}
                    </TextField>
                    <TextField
                      select
                      label="System Action"
                      value={systemAction}
                      onChange={(e) => setSystemAction(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="allow">Allow</MenuItem>
                      <MenuItem value="social_assessment">Social Assessment</MenuItem>
                      <MenuItem value="review">Review</MenuItem>
                      <MenuItem value="reject">Reject</MenuItem>
                    </TextField>
                  </Box>
                  <Box display="flex" style={{ gap: 12 }}>
                    <TextField
                      select
                      label="Gender"
                      value={genderScope}
                      onChange={(e) => setGenderScope(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="any">Any</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </TextField>
                    <TextField
                      label="Min Age (years)"
                      value={minAge}
                      onChange={(e) => setMinAge(e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                    <TextField
                      label="Max Age (years)"
                      value={maxAge}
                      onChange={(e) => setMaxAge(e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} color="primary" />}
                    label="Enabled"
                  />
                </Box>
              </CardContent>
            </Card>

            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Eligible Services (tblServices)</Typography>
                <Typography style={{ color: "#4b5563", marginBottom: 12 }}>
                  Select one or more services that become eligible when all conditions pass.
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 10 }}>
                  <Typography style={{ fontWeight: 600 }}>
                    Loaded services: {services.length} | Selected: {selectedServiceIds.length}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Add />}
                    onClick={openServiceDialog}
                    disabled={!services.length}
                  >
                    Select Services
                  </Button>
                </Box>
                {!services.length ? (
                  <Typography style={{ color: "#d32f2f" }}>
                    No services loaded yet. Click "Load Diagnosis & Services" in Connection first.
                  </Typography>
                ) : !selectedServices.length ? (
                  <Typography>No services selected</Typography>
                ) : (
                  <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                    {selectedServices.map((service) => {
                      const id = entityUuid(service, ["service_id"]);
                      if (!id) return null;
                      return (
                        <Chip
                          key={id}
                          label={`${service.code || ""} - ${service.name || ""}`}
                          onDelete={() => setSelectedServiceIds((prev) => prev.filter((x) => x !== id))}
                        />
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Screen Fields</Typography>
                {!fields.length && (
                  <Typography style={{ marginBottom: 12 }}>
                    Add fields like Boolean, Text, Number, Choice buttons, or Dropdown.
                  </Typography>
                )}
                {[...fields]
                  .sort((a, b) => a.order - b.order)
                  .map((field) => (
                    <Box
                      key={field.key}
                      display="flex"
                      justifyContent="space-between"
                      style={{
                        marginBottom: 10,
                        padding: 14,
                        borderRadius: 14,
                        border: "1px solid #E5E7EB",
                        background: "#fff",
                      }}
                    >
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: 18, fontWeight: 700 }}>{field.label}</Typography>
                        <Box display="flex" style={{ gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                          <Typography style={{ color: "#808080" }}>{FIELD_TYPE_LABELS[field.type] || field.type}</Typography>
                          {field.required && <Typography style={{ color: "#1d4ed8", fontWeight: 600 }}>required</Typography>}
                          {!!field.indication?.trim() && (
                            <Typography style={{ color: "#0E6BA8", fontWeight: 700, fontSize: 12 }}>
                              ↪ {field.indication}
                            </Typography>
                          )}
                          {!!field.section?.trim() && <Typography style={{ color: "#0f766e", fontWeight: 600 }}>{field.section}</Typography>}
                          <Typography style={{ color: "#808080" }}>order {field.order}</Typography>
                          {field.computedType === "date_diff_days" && (
                            <Typography style={{ color: "#4338ca", fontWeight: 600 }}>
                              auto: {field.computedToField} {field.computedOperator || "-"} {field.computedFromField}
                            </Typography>
                          )}
                          {field.computedType === "binary_formula" && (
                            <Typography style={{ color: "#4338ca", fontWeight: 600 }}>
                              auto: {field.computedToField} {field.computedOperator || "-"} {field.computedFromField}
                            </Typography>
                          )}
                          {field.computedType === "formula" && (
                            <Typography style={{ color: "#4338ca", fontWeight: 600 }}>
                              formula: {field.computedFormula}
                            </Typography>
                          )}
                        </Box>
                        {(field.helperText?.trim() || field.unit?.trim()) && (
                          <Typography style={{ color: "#4b5563", marginTop: 6 }}>
                            {[field.helperText, field.unit].filter((x) => x && x.trim()).join(" • ")}
                          </Typography>
                        )}
                        {(field.visibleWhen.length || field.requiredWhen.length) && (
                          <Box display="flex" style={{ gap: 8, marginTop: 6 }}>
                            {!!field.visibleWhen.length && (
                              <Typography style={{ color: "#ea580c", fontWeight: 600 }}>
                                show when {field.visibleWhen.length}
                              </Typography>
                            )}
                            {!!field.requiredWhen.length && (
                              <Typography style={{ color: "#9333ea", fontWeight: 600 }}>
                                require when {field.requiredWhen.length}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {!!field.choices.length && (
                          <Box display="flex" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                            {field.choices.map((choice) => (
                              <Chip key={`${field.key}-${choice}`} label={choice} />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <IconButton onClick={() => openFieldDialog(field)}>
                          <EditOutlined />
                        </IconButton>
                        <IconButton onClick={() => removeField(field.key)}>
                          <Close style={{ color: "#ef4444" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                <Button variant="outlined" startIcon={<Add />} onClick={() => openFieldDialog(null)}>
                  Add Field
                </Button>
              </CardContent>
            </Card>

            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Conditions</Typography>
                <Typography style={{ color: "#4b5563", marginBottom: 12 }}>
                  Click AND/OR between conditions to toggle the logic. Example: Age &lt; 18 OR Gravida &gt;= 1
                </Typography>
                {!conditions.length && <Typography style={{ marginBottom: 12 }}>No conditions added yet.</Typography>}
                {conditions.map((condition, index) => (
                  <Box key={`rule-condition-${index}`} style={{ marginBottom: 10 }}>
                    {index > 0 && (
                      <Box
                        onClick={() => {
                          const next = [...conditions];
                          next[index] = { ...next[index], connector: next[index].connector === "or" ? "and" : "or" };
                          setConditions(next);
                        }}
                        style={{
                          background: condition.connector === "or" ? "#EDE9FE" : "#FEF3C7",
                          borderRadius: 6,
                          padding: "4px 10px",
                          display: "inline-block",
                          marginBottom: 6,
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        title="Click to toggle AND / OR"
                      >
                        <Typography style={{ fontSize: 12, fontWeight: 800, color: condition.connector === "or" ? "#5B21B6" : "#92400E" }}>
                          {(condition.connector || "and").toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      display="flex"
                      style={{
                        gap: 10,
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid #E5E7EB",
                        background: "#fff",
                      }}
                    >
                    <TextField
                      select
                      label="Field"
                      value={condition.field}
                      onChange={(e) => {
                        const next = [...conditions];
                        next[index] = { ...next[index], field: e.target.value };
                        setConditions(next);
                      }}
                      style={{ flex: 3 }}
                    >
                      {conditionFieldOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Operator"
                      value={condition.operator}
                      onChange={(e) => {
                        const next = [...conditions];
                        next[index] = { ...next[index], operator: e.target.value };
                        setConditions(next);
                      }}
                      style={{ flex: 2 }}
                    >
                      {CONDITION_OPERATORS.map((op) => (
                        <MenuItem key={op} value={op}>{op}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Value"
                      value={condition.value}
                      onChange={(e) => {
                        const next = [...conditions];
                        next[index] = { ...next[index], value: e.target.value };
                        setConditions(next);
                      }}
                      style={{ flex: 3 }}
                    />
                    <IconButton onClick={() => setConditions((prev) => prev.filter((_, i) => i !== index))}>
                      <DeleteOutline style={{ color: "#ef4444" }} />
                    </IconButton>
                    </Box>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  disabled={!conditionFieldOptions.length}
                  onClick={() => setConditions((prev) => [...prev, { field: conditionFieldOptions[0].key, operator: "eq", value: "", connector: "and" }])}
                >
                  Add Condition
                </Button>
              </CardContent>
            </Card>

            <Card style={{ borderRadius: 20, marginBottom: 16 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Mandatory Attachments</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mandatoryAttachmentRequired}
                      onChange={(e) => setMandatoryAttachmentRequired(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Claim requires attachment when this rule matches"
                />
                <Typography style={{ color: "#4b5563", marginTop: 8 }}>
                  Recommended: Keep this ON for diagnosis/service combinations that must include supporting documents.
                </Typography>
              </CardContent>
            </Card>

            <Button variant="contained" color="primary" onClick={saveRule} disabled={loading} startIcon={<Add />}>
              {selectedRuleId ? "Update Rule" : "Create Rule"}
            </Button>
          </Box>

          <Box style={{ flex: 4, minWidth: 320 }}>
            <Card style={{ borderRadius: 20 }}>
              <CardContent>
                <Typography style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Existing Rules</Typography>
                {!selectedBenefitPlanId && (
                  <Typography style={{ color: "#4b5563", marginBottom: 10 }}>
                    Select a benefit plan and click Load Existing Rules
                  </Typography>
                )}
                {!rules.length ? (
                  <Box style={{ padding: "12px 0" }}>
                    <Typography align="center">No rules loaded</Typography>
                  </Box>
                ) : (
                  rules.map((rule, index) => {
                    const diagnosis = asStringMap(rule.diagnosis);
                    const diagnosisText = !Object.keys(diagnosis).length
                      ? "Any diagnosis"
                      : `${diagnosis.code || ""} - ${diagnosis.name || ""}`;
                    const ruleServices = Array.isArray(rule.services) ? rule.services : [];
                    const servicesText = ruleServices.length
                      ? ruleServices.map((s) => s.code || s.name || "?").join(", ")
                      : "No services";
                    return (
                      <Card key={entityUuid(rule, ["rule_id"]) || index} style={{ marginBottom: 10 }}>
                        <CardContent style={{ paddingBottom: 12 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Typography style={{ fontWeight: 600 }}>{rule.name || "-"}</Typography>
                              <Typography style={{ color: "#4b5563", marginTop: 4 }}>
                                {diagnosisText}
                                <br />
                                {`${servicesText} • ${rule.system_action}`}
                              </Typography>
                            </Box>
                            <IconButton onClick={() => editRule(rule)}>
                              <EditOutlined />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* ── Add / Edit Diagnosis dialog ────────────────────────────────── */}
      <Dialog open={diagnosisDialogOpen} onClose={() => setDiagnosisDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography style={{ fontSize: 18, fontWeight: 800 }}>
            {diagnosisEditingId ? "Edit Diagnosis" : "Add Diagnosis"}
          </Typography>
          <IconButton size="small" onClick={() => setDiagnosisDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
            <TextField
              label="Code *"
              value={diagnosisDraftCode}
              onChange={(e) => setDiagnosisDraftCode(e.target.value.toUpperCase().slice(0, 6))}
              inputProps={{ maxLength: 6 }}
              helperText="Up to 6 characters, e.g. A00"
              fullWidth
              autoFocus
            />
            <TextField
              label="Name *"
              value={diagnosisDraftName}
              onChange={(e) => setDiagnosisDraftName(e.target.value)}
              helperText="Full diagnosis name"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px", gap: 8 }}>
          <Button onClick={() => setDiagnosisDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!diagnosisDraftCode.trim() || !diagnosisDraftName.trim() || loading}
            onClick={saveDiagnosis}
            style={{ fontWeight: 700, textTransform: "none" }}
          >
            {diagnosisEditingId ? "Save Changes" : "Create Diagnosis"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Services</DialogTitle>
        <DialogContent>
          <TextField
            label="Search service"
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
            fullWidth
            InputProps={{ startAdornment: <Search style={{ marginRight: 8, color: "#666" }} /> }}
          />
          <Box style={{ maxHeight: 420, overflowY: "auto", marginTop: 12 }}>
            {filteredServices.map((service, idx) => {
              const id = entityUuid(service, ["service_id"]);
              if (!id) return null;
              return (
                <FormControlLabel
                  key={id || idx}
                  control={
                    <Checkbox
                      checked={serviceDraftIds.includes(id)}
                      onChange={(e) => toggleServiceDraft(id, e.target.checked)}
                      color="primary"
                    />
                  }
                  label={`${service.code || ""} - ${service.name || ""}`}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDraftIds([])} disabled={serviceDraftIds.length === 0}>
            Clear All
          </Button>
          <Button onClick={() => setServiceDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setSelectedServiceIds([...serviceDraftIds]);
              setServiceDialogOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{fieldEditingKey ? "Edit Field" : "Add Field"}</DialogTitle>
        <DialogContent>
          <Box display="grid" gridGap={12}>
            <TextField
              label="Field Name"
              value={fieldDraft.label}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, label: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Field Name (Myanmar) — optional"
              value={fieldDraft.labelMy || ""}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, labelMy: e.target.value }))}
              placeholder="မြန်မာဘာသာဖြင့် အမည်"
              fullWidth
            />
            <TextField
              label="Indication (clinical grouping, e.g. Abnormal Presentation)"
              value={fieldDraft.indication}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, indication: e.target.value }))}
              fullWidth
              helperText="Groups this field under a clinical indication on the tablet (above Section). Leave blank for un-grouped fields."
            />
            <TextField
              label="Section / Group"
              value={fieldDraft.section}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, section: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Field Type"
              value={fieldDraft.type}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, type: e.target.value }))}
              fullWidth
            >
              {Object.entries(FIELD_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Order"
              value={fieldDraft.order}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, order: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Helper Text"
              value={fieldDraft.helperText}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, helperText: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Unit / Hint suffix"
              value={fieldDraft.unit}
              onChange={(e) => setFieldDraft((prev) => ({ ...prev, unit: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={fieldDraft.required}
                  onChange={(e) => setFieldDraft((prev) => ({ ...prev, required: e.target.checked }))}
                  color="primary"
                />
              }
              label="Required"
            />

            {fieldDraft.type === "number" && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(fieldDraft.computedType)}
                      onChange={(e) =>
                        setFieldDraft((prev) => ({
                          ...prev,
                          computedType: e.target.checked ? "binary_formula" : "",
                          computedOperator: e.target.checked ? (prev.computedOperator || "-") : "",
                          computedToField: e.target.checked ? prev.computedToField : "",
                          computedFromField: e.target.checked ? prev.computedFromField : "",
                          computedFormula: e.target.checked ? prev.computedFormula : "",
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Compute value automatically"
                />
                {!!fieldDraft.computedType && (
                  <>
                    <TextField
                      select
                      label="Compute mode"
                      value={fieldDraft.computedType === "formula" ? "formula" : "binary"}
                      onChange={(e) =>
                        setFieldDraft((prev) => ({
                          ...prev,
                          computedType: e.target.value === "formula" ? "formula" : "binary_formula",
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="binary">Simple: A operator B</MenuItem>
                      <MenuItem value="formula">Formula: custom expression</MenuItem>
                    </TextField>

                    {fieldDraft.computedType === "formula" ? (
                      <TextField
                        label="Formula"
                        placeholder="e.g. total_income / household_members * 30"
                        helperText="Use field keys and + - * / ( ). Example: (income + allowance) / members"
                        value={fieldDraft.computedFormula}
                        onChange={(e) => setFieldDraft((prev) => ({ ...prev, computedFormula: e.target.value }))}
                        fullWidth
                      />
                    ) : computableFields.length < 2 ? (
                      <Typography style={{ color: "#d32f2f" }}>
                        Add at least two numeric/date fields first, then select A, operator, and B.
                      </Typography>
                    ) : (
                      <>
                        <TextField
                          select
                          label="A field"
                          value={fieldDraft.computedToField}
                          onChange={(e) => setFieldDraft((prev) => ({ ...prev, computedToField: e.target.value }))}
                          fullWidth
                        >
                          {computableFields.map((field) => (
                            <MenuItem key={`left-${field.key}`} value={field.key}>
                              {`A: ${field.label} (${field.key})`}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Operator"
                          value={fieldDraft.computedOperator || "-"}
                          onChange={(e) => setFieldDraft((prev) => ({ ...prev, computedOperator: e.target.value }))}
                          fullWidth
                        >
                          <MenuItem value="-">−</MenuItem>
                          <MenuItem value="+">+</MenuItem>
                          <MenuItem value="*">×</MenuItem>
                          <MenuItem value="/">÷</MenuItem>
                        </TextField>
                        <TextField
                          select
                          label="B field"
                          value={fieldDraft.computedFromField}
                          onChange={(e) => setFieldDraft((prev) => ({ ...prev, computedFromField: e.target.value }))}
                          fullWidth
                        >
                          {computableFields.map((field) => (
                            <MenuItem key={`right-${field.key}`} value={field.key}>
                              {`B: ${field.label} (${field.key})`}
                            </MenuItem>
                          ))}
                        </TextField>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {(fieldDraft.type === "choice_buttons" || fieldDraft.type === "choice_dropdown") && (
              <>
                <Typography style={{ fontWeight: 600 }}>Choices</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(fieldDraft.multiple)}
                      onChange={(e) => setFieldDraft((prev) => ({ ...prev, multiple: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Allow multiple selections (officer can pick more than one option)"
                />
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField
                    placeholder="Add choice..."
                    value={choiceDraft}
                    onChange={(e) => setChoiceDraft(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const value = choiceDraft.trim();
                      if (!value) return;
                      setFieldDraft((prev) => ({ ...prev, choices: [...prev.choices, value] }));
                      setChoiceDraft("");
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                  {fieldDraft.choices.map((choice) => (
                    <Chip
                      key={`choice-${choice}`}
                      label={choice}
                      onDelete={() =>
                        setFieldDraft((prev) => ({ ...prev, choices: prev.choices.filter((item) => item !== choice) }))
                      }
                    />
                  ))}
                </Box>
              </>
            )}

            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={() => openConditionListDialog("Show field when", "visibleWhen", fieldDraft.visibleWhen)}
              >
                Show when ({fieldDraft.visibleWhen.length})
              </Button>
              <Button
                variant="outlined"
                onClick={() => openConditionListDialog("Require field when", "requiredWhen", fieldDraft.requiredWhen)}
              >
                Require when ({fieldDraft.requiredWhen.length})
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveFieldDialog}>Save Field</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conditionDialogOpen} onClose={() => setConditionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{conditionDialogTitle}</DialogTitle>
        <DialogContent>
          {!conditionDialogDraft.length && (
            <Typography style={{ marginBottom: 12 }}>
              No conditions yet. Add one to make the field show or become mandatory only in specific cases.
            </Typography>
          )}
          {conditionDialogDraft.map((item, index) => (
            <Box key={`cond-draft-${index}`} display="flex" style={{ gap: 10, marginBottom: 10 }}>
              <TextField
                select
                label="Field"
                value={item.field}
                onChange={(e) => {
                  const next = [...conditionDialogDraft];
                  next[index] = { ...next[index], field: e.target.value };
                  setConditionDialogDraft(next);
                }}
                style={{ flex: 3 }}
              >
                {conditionFieldOptions.map((option) => (
                  <MenuItem key={`dlg-${option.key}`} value={option.key}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Operator"
                value={item.operator}
                onChange={(e) => {
                  const next = [...conditionDialogDraft];
                  next[index] = { ...next[index], operator: e.target.value };
                  setConditionDialogDraft(next);
                }}
                style={{ flex: 2 }}
              >
                {CONDITION_OPERATORS.map((op) => (
                  <MenuItem key={`dlg-op-${op}`} value={op}>{op}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Value"
                value={item.value}
                onChange={(e) => {
                  const next = [...conditionDialogDraft];
                  next[index] = { ...next[index], value: e.target.value };
                  setConditionDialogDraft(next);
                }}
                style={{ flex: 3 }}
              />
              <IconButton onClick={() => setConditionDialogDraft((prev) => prev.filter((_, i) => i !== index))}>
                <DeleteOutline style={{ color: "#ef4444" }} />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              setConditionDialogDraft((prev) => [
                ...prev,
                { field: conditionFieldOptions[0]?.key || "insuree.age", operator: "eq", value: "" },
              ])
            }
          >
            Add Condition
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConditionDialogOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={applyConditionListDialog}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
